import { LearnizRequirementTree } from './requirements-tree';
import {
  BackgroundImages,
  BackgroundPatternImages,
  BackgroundType,
  CustomImage,
  Font,
  menuEditionStatusFactory,
  Perimeter,
  ProductError,
  ProductErrorType,
  ProductGraphicPreferenceStatus,
  ProductMenuEditionStatus,
  ProductMenuValidationStep,
  ScoreFindStrategy,
  SSOLink,
  SSOLinkMode,
  TextEntry,
} from '../models';
import { LearnizProgression } from './progression';
import { LearnizMedia, LearnizMediaType } from './media';
import {
  AccordionData,
  AttachmentData,
  BoxContentType,
  CardModalData,
  ClickableSchemaData,
  DocumentViewerData,
  DownloadTableData,
  FlipCardData,
  GameOptions,
  MediaPlayerData,
  SpacerData,
  TextBoxData,
  VerticalMenuData,
} from './interactivities';
import { ContentDeployableType } from './content-deployable';

export class LearnizSession {
  learniz: LearnizModel;
  progress: LearnizProgression;
  requirementsTree: LearnizRequirementTree;
  clientId?: number;
  perimeter?: Perimeter;
  mode: SSOLinkMode;
  backofficeUrl?: string;

  constructor(session: LearnizSession, productPerimeterSlug?: string) {
    this.learniz = new LearnizModel(session.learniz, productPerimeterSlug);
    this.progress = new LearnizProgression(session.progress);
    this.requirementsTree = new LearnizRequirementTree(session.requirementsTree);
    this.clientId = session.clientId;
    this.mode = session.mode || SSOLinkMode.NORMAL;
  }
}

export class LearnizModel {
  slug: string; // productSlug (as it's the property name send by API, it can not be renamed)
  productPerimeterSlug?: string; // only available through learniz.state.ts which use productPerimeterSlug
  name: string;
  local: string;
  context: LearnizContext;
  params: LearnizParams;
  chapters: LearnizChapter[];
  defaultTexts: TextEntry[];
  validators: LearnizValidator[];
  version: string;
  publishedDate?: string;
  demo?: boolean;
  threadSlug?: string;

  // for edition
  editionInfo?: LearnizEditionInfo;
  validationConditions?: LearnizValidationConditions;

  constructor(learnizModel?: LearnizModel, productPerimeterSlug?: string, version?: string) {
    this.validators = (learnizModel?.validators || []).map(r => new LearnizValidator(r));
    this.slug = learnizModel?.slug;
    this.productPerimeterSlug = productPerimeterSlug || learnizModel?.productPerimeterSlug;
    this.name = learnizModel?.name;
    this.local = learnizModel?.local;
    this.context = new LearnizContext(learnizModel?.context);
    this.params = new LearnizParams(learnizModel?.params);
    this.chapters = (learnizModel?.chapters || []).map(c => new LearnizChapter(c));
    this.defaultTexts = (learnizModel?.defaultTexts || []).map(t => new TextEntry(t));
    this.version = learnizModel?.version || version;
    this.publishedDate = learnizModel?.publishedDate;
    this.editionInfo = new LearnizEditionInfo(learnizModel?.editionInfo);
    this.validationConditions = new LearnizValidationConditions(learnizModel?.validationConditions);
    this.demo = learnizModel?.demo || false;
    this.threadSlug = learnizModel?.threadSlug || null;
  }
}

export class LearnizEditionInfo {
  paramsStatus: ProductMenuEditionStatus;
  contentStatus: ProductMenuEditionStatus;
  validationStatus: ProductMenuEditionStatus;
  publicationStatus: ProductMenuEditionStatus;
  ssoLinks: SSOLink[];
  validationStep: ProductMenuValidationStep;

  constructor(info?: LearnizEditionInfo) {
    this.paramsStatus = menuEditionStatusFactory(info?.paramsStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.contentStatus = menuEditionStatusFactory(info?.contentStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.validationStatus = menuEditionStatusFactory(info?.validationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.publicationStatus = menuEditionStatusFactory(info?.publicationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.ssoLinks = (info?.ssoLinks || []).map(link => new SSOLink(link));
    this.validationStep = info?.validationStep ?? ProductMenuValidationStep.CONDITIONS;
  }
}

export class LearnizValidationConditions {
  chainedRequirements: boolean;
  validAll: boolean;
  gameMinimalScore: number;
  deliverCertificate: boolean;

  constructor(conditions?: LearnizValidationConditions) {
    this.chainedRequirements = !!conditions?.chainedRequirements || false;
    this.validAll = !!conditions?.validAll || false;
    this.gameMinimalScore = conditions?.gameMinimalScore;
    this.deliverCertificate = !!conditions?.deliverCertificate || false;
  }
}

export abstract class LearnizElement {
  requirements: LearnizRequirement[];
  validators: LearnizValidator[];

  protected constructor(requirements: LearnizRequirement[], validators: LearnizValidator[]) {
    this.requirements = (requirements || []).map(r => new LearnizRequirement(r));
    this.validators = (validators || []).map(v => new LearnizValidator(v));
  }
}

export class LearnizContext {
  title: string;
  description: string;
  objectives: string;
  requirements: string;
  endSentence: string;
  author: string;
  period?: LearnizPeriodDates;
  hasVideo: boolean;
  hasPodcast: boolean;
  hasGame: boolean;
  hasAttachmentDownload: boolean;
  hasTableDownload: boolean;
  durationSeconds?: number;

  constructor(context?: LearnizContext) {
    this.title = context?.title ?? null;
    this.description = context?.description ?? null;
    this.objectives = context?.objectives ?? null;
    this.requirements = context?.requirements ?? null;
    this.endSentence = context?.endSentence ?? null;
    this.author = context?.author ?? null;
    this.period = !!context?.period ? new LearnizPeriodDates(context.period.start, context.period.end) : null;
    this.hasVideo = context?.hasVideo ?? null;
    this.hasAttachmentDownload = context?.hasAttachmentDownload ?? null;
    this.hasGame = context?.hasGame ?? null;
    this.hasPodcast = context?.hasPodcast ?? null;
    this.hasTableDownload = context?.hasTableDownload ?? null;
    this.durationSeconds = context?.durationSeconds ?? null;
  }
}

export class LearnizParams {
  chained: boolean;
  gameScoreStrategy: ScoreFindStrategy;
  styleParams: LearnizStyleParams;
  hideLandingMainScreen: boolean;
  hideStats: boolean;
  hideLandingContent: boolean;
  hideLandingMenu: boolean;
  disableIdle: boolean;
  hidePrivacy: boolean;
  menuInitiallyOpen: boolean;
  hideTopBar: boolean;
  animated: boolean;
  hideHelp: boolean;
  hideEndStats: boolean;
  hideTopBarProgress: boolean;
  hideLandingFormationTime: boolean;

  constructor(config?: LearnizParams) {
    this.chained = config?.chained || false;
    this.gameScoreStrategy = config?.gameScoreStrategy || ScoreFindStrategy.BEST_SCORE;
    this.styleParams = config ? new LearnizStyleParams(config.styleParams) : null;
    this.hideStats = config?.hideStats || false;
    this.hideLandingContent = config?.hideLandingContent || false;
    this.hideLandingMenu = config?.hideLandingMenu || false;
    this.hideLandingMainScreen = config?.hideLandingMainScreen || false;
    this.hidePrivacy = config?.hidePrivacy || false;
    this.disableIdle = config?.disableIdle || false;
    this.menuInitiallyOpen = config?.menuInitiallyOpen || false;
    this.hideTopBar = config?.hideTopBar || false;
    this.animated = config?.animated || false;
    this.hideHelp = config?.hideHelp || false;
    this.hideEndStats = config?.hideEndStats || false;
    this.hideTopBarProgress = config?.hideTopBarProgress || false;
    this.hideLandingFormationTime = config?.hideLandingFormationTime || false;
  }
}

export class LearnizStyleParams {
  primaryColor?: string;
  darkColor?: string;
  lightColor?: string;
  faviconUrl?: CustomImage;

  fontTitle?: Font;
  status: ProductGraphicPreferenceStatus;

  landingBackgroundImages?: {
    main?: CustomImage;
    menu?: CustomImage;
  };

  logoImage?: CustomImage | null;

  slideBackground?: CustomImage;
  themesCSS?: string[];

  constructor(params?: LearnizStyleParams) {
    this.primaryColor = params?.primaryColor || '#43ae97';
    this.lightColor = params?.lightColor || '#FFFFFF';
    this.darkColor = params?.darkColor || '#000000';
    this.status = params?.status ? params.status : ProductGraphicPreferenceStatus.DEFAULT;
    this.faviconUrl = new CustomImage(
      params?.faviconUrl?.src
        ? params.faviconUrl
        : {
            src: 'assets/shared/images/branding/favicon-z.svg',
            type: BackgroundType.IMAGE,
          },
    );

    this.fontTitle = new Font(params?.fontTitle || { family: "'Ubuntu', sans serif" });

    this.landingBackgroundImages = {
      main: new CustomImage(params?.landingBackgroundImages?.main || BackgroundImages[0]),
      menu: new CustomImage(params?.landingBackgroundImages?.menu || BackgroundPatternImages[0]),
    };

    this.logoImage = params?.logoImage ? new CustomImage(params.logoImage) : null;

    // the null value is important in case of no background to display
    // the default lottie when there is no slideBackground and the light color is white
    this.slideBackground = new CustomImage(params?.slideBackground || BackgroundPatternImages[0]);
    this.themesCSS = params?.themesCSS || [];
  }
}

export class LearnizChapter extends LearnizElement {
  slug: string;
  title: string;
  description: string;
  recommendedDuration: string;
  slides: LearnizSlide[];

  constructor(chapter?: LearnizChapter) {
    if (chapter) {
      super(chapter.requirements, chapter.validators);
      this.slug = chapter.slug;
      this.title = chapter.title;
      this.description = chapter.description;
      this.recommendedDuration = chapter.recommendedDuration;
      this.slides = (chapter.slides || []).map(s => new LearnizSlide(s));
    } else {
      super([], []);
      this.slug = null;
      this.title = null;
      this.description = null;
      this.recommendedDuration = null;
      this.slides = [];
    }
  }

  static validate(chapter: LearnizChapter, payloadData: any): ProductError[] {
    const error: ProductError[] = [];
    if (!chapter.slides || !chapter.slides.length) {
      error.push({
        type: ProductErrorType.MISSING,
        property: 'slides',
        payload: {
          ...payloadData,
        },
      });
    }

    if (!chapter.title || !chapter.title.length) {
      error.push({
        type: ProductErrorType.MISSING,
        property: 'slideTitle',
        payload: {
          ...payloadData,
        },
      });
    }

    const errorsInSlide = chapter.slides
      .map(
        (
          slide,
          slideIndex, //
        ) =>
          LearnizSlide.validate(slide, {
            ...payloadData,
            slideIndex,
          }),
      )
      .reduce((flattenedArray: any[], subArray) => {
        flattenedArray.push(...subArray);
        return flattenedArray;
      }, []);
    error.push(...errorsInSlide);
    return error;
  }
}

export class LearnizSlide extends LearnizElement {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  interactivities: LearnizInteractivity[];
  parameters: SlideParameters;

  constructor(slide?: LearnizSlide) {
    super(slide?.requirements ?? [], slide?.validators ?? []);
    this.slug = slide?.slug;
    this.subtitle = slide?.subtitle;
    this.description = slide?.description;
    this.title = slide?.title;
    this.interactivities = (slide?.interactivities || []).map(i => new LearnizInteractivity(i));
    this.parameters = new SlideParameters(slide?.parameters);
  }

  static validate(slide: LearnizSlide, payloadData: any): ProductError[] {
    const error: ProductError[] = [];
    if (!slide.interactivities || !slide.interactivities.length) {
      error.push({
        type: ProductErrorType.MISSING,
        property: 'interactivities',
        payload: {
          ...payloadData,
        },
      });
    }
    if (!slide.title || !slide.title.length) {
      error.push({
        type: ProductErrorType.MISSING,
        property: 'interactivitieTitle',
        payload: {
          ...payloadData,
        },
      });
    }

    const errorsInSlides = slide.interactivities
      .map((interactivity, interactivityIndex) =>
        LearnizInteractivity.validate(interactivity, {
          ...payloadData,
          interactivityIndex,
        }),
      )
      .reduce((flattenedArray: any[], subArray) => {
        flattenedArray.push(...subArray);
        return flattenedArray;
      }, []);
    error.push(...errorsInSlides);
    return error;
  }
}

export class LearnizInteractivity {
  slug: string;
  type: LearnizInteractivityType;
  medias: LearnizMedia<any>[];
  data: any;
  weight?: number;

  constructor(interactivity?: LearnizInteractivity) {
    this.slug = interactivity?.slug;
    this.weight = interactivity?.weight;
    this.type = interactivity?.type;
    this.medias = (interactivity?.medias || []).map(m => new LearnizMedia(m));
    if (interactivity) {
      this.data = cloneData(interactivity.type, interactivity.data);
    } else {
      this.data = null;
    }
  }

  static validate(interactivity: LearnizInteractivity, payloadData: any): ProductError[] {
    switch (interactivity.type) {
      case LearnizInteractivityType.TEXT_BOX:
        return TextBoxData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.MEDIA_PLAYER:
        return MediaPlayerData.validate(interactivity.data, interactivity.medias, payloadData);
      case LearnizInteractivityType.GAME:
        return GameOptions.validate(interactivity.data, interactivity.medias, payloadData);
      case LearnizInteractivityType.FLIP_CARD:
        return FlipCardData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.DOWNLOAD_TABLE:
        return [];
      case LearnizInteractivityType.DOCUMENT_VIEWER:
        return DocumentViewerData.validate(interactivity.data, interactivity.medias, payloadData);
      case LearnizInteractivityType.CLICKABLE_SCHEMA:
        return ClickableSchemaData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.CARD_MODAL:
        return CardModalData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.ATTACHMENT:
        return AttachmentData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.SPACER:
        return SpacerData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.ACCORDION:
        return AccordionData.validate(interactivity.data, {
          ...payloadData,
        });
      case LearnizInteractivityType.VERTICAL_MENU:
        return VerticalMenuData.validate(interactivity.data, {
          ...payloadData,
        });
      default:
        console.error('Uncatch case', interactivity);

        return [
          {
            type: ProductErrorType.BAD_FORMAT,
            property: 'type',
            payload: {
              ...payloadData,
            },
          },
        ];
    }
  }
}

function cloneData(type: LearnizInteractivityType, data: any): any {
  switch (type) {
    case LearnizInteractivityType.TEXT_BOX:
      return new TextBoxData(data);
    case LearnizInteractivityType.MEDIA_PLAYER:
      return new MediaPlayerData(data);
    case LearnizInteractivityType.FLIP_CARD:
      return new FlipCardData(data);
    case LearnizInteractivityType.DOWNLOAD_TABLE:
      return new DownloadTableData(data);
    case LearnizInteractivityType.CLICKABLE_SCHEMA:
      return new ClickableSchemaData(data);
    case LearnizInteractivityType.CARD_MODAL:
      return new CardModalData(data);
    case LearnizInteractivityType.ATTACHMENT:
      return new AttachmentData(data);
    case LearnizInteractivityType.GAME:
      return new GameOptions(data);
    case LearnizInteractivityType.SPACER:
      return new SpacerData(data);
    case LearnizInteractivityType.DOCUMENT_VIEWER: // no data for document-viewer (every thing is in the medias)
      return null;
    case LearnizInteractivityType.ACCORDION:
      return new AccordionData(data);
    case LearnizInteractivityType.VERTICAL_MENU:
      return new VerticalMenuData(data);
    default:
      console.error('Cannot clone', type);
  }
}

// Use to display interactivity icons
export enum LearnizInteractivityType {
  FLIP_CARD = 'flip-card',
  DOCUMENT_VIEWER = 'document-viewer',
  GAME = 'game',
  CARD_MODAL = 'card-modal',
  MEDIA_PLAYER = 'media-player',
  TEXT_BOX = 'text-box',
  DOWNLOAD_TABLE = 'download-table',
  CLICKABLE_SCHEMA = 'clickable-schema',
  ATTACHMENT = 'attachment',
  SPACER = 'spacer',
  ACCORDION = 'accordion',
  VERTICAL_MENU = 'vertical-menu',
}

export class LearnizValidator {
  minimumAverageScore?: number;
  elements: LearnizValidatorElement[];
  generatedFromGeneralConditions?: boolean;

  constructor(learnizValidator: LearnizValidator) {
    this.minimumAverageScore = learnizValidator.minimumAverageScore || null;
    this.elements = (learnizValidator.elements || []).map(e => new LearnizValidatorElement(e));
    this.generatedFromGeneralConditions = learnizValidator.generatedFromGeneralConditions || false;
  }
}

export class LearnizValidatorElement {
  slug: string;
  type: LearnizValidatorType;
  score?: number;

  constructor(element?: LearnizValidatorElement) {
    if (element) {
      this.slug = element.slug;
      this.type = element.type;

      if (this.type === LearnizValidatorType.SCORE) {
        this.score = element.score;
      }
    } else {
      this.slug = null;
      this.type = null;
    }
  }
}

export class LearnizRequirement {
  elements: RequirementElement[];
  generatedFromGeneralConditions?: boolean;

  constructor(learnizRequirement?: LearnizRequirement) {
    if (learnizRequirement) {
      this.elements = (learnizRequirement.elements || []).map(e => new RequirementElement(e));
      this.generatedFromGeneralConditions = learnizRequirement.generatedFromGeneralConditions || false;
    } else {
      this.elements = [];
      this.generatedFromGeneralConditions = false;
    }
  }
}

export class RequirementElement {
  chapterSlug: string;
  slideSlug?: string;
  interactivitySlug?: string;
  type: LearnizRequirementType;
  score?: number;

  constructor(element?: RequirementElement) {
    this.chapterSlug = element?.chapterSlug;
    this.slideSlug = element?.slideSlug;
    this.interactivitySlug = element?.interactivitySlug;
    this.type = element?.type;
    this.score = element?.score;
  }
}

export enum LearnizRequirementType {
  STATUS = 'status',
  SCORE = 'score',
}

export enum LearnizValidatorType {
  STATUS = 'status',
  SCORE = 'score',
}

export class LearnizPeriodDates {
  start: Date;
  end: Date;

  constructor(start: Date, end: Date) {
    this.start = start;
    this.end = end;
  }
}

export interface LearnizChaptersSummary {
  numberOfSlides: number;
  numberOfVideos: number;
  numberOfPodcasts: number;
  numberOfGames: number;
  numberOfDocuments: number;
  numberOfUrls: number;
}

export class SlideParameters {
  customBackground: CustomImage;

  constructor(parameters: SlideParameters) {
    this.customBackground = parameters?.customBackground ? new CustomImage(parameters.customBackground) : null;
  }
}

export interface LearnizSelectorInteractivityAllowedTypes {
  textBox: {
    allow: boolean;
    subType?: BoxContentType[];
  };
  mediaPlayer: {
    allow: boolean;
    subType?: LearnizMediaType[];
  };
  table: {
    allow: boolean;
    subType?: [];
  };
  download: {
    allow: boolean;
    subType?: [];
  };
  schema: {
    allow: boolean;
    subType?: [];
  };
  card: {
    allow: boolean;
    subType?: [];
  };
  game: {
    allow: boolean;
    subType?: [];
  };
  contentDeployable: {
    allow: boolean;
    subType?: ContentDeployableType[];
  };
}
